import { computed, toValue } from 'vue'
import { MIME_TYPES } from '@deepcloud/deep-ui-utils'
import type { MimeTypes } from '@deepcloud/deep-ui-utils'

export const DEFAULT_PREVIEW_WIDTH = 700

export const HTML_PREVIEW_MAX_SIZE = 2_000_000 // ~2MB

const JSON_PREVIEW_MAX_SIZE = 500_000 // ~0.5MB

function isTouchDevice() {
  return window.matchMedia('(pointer: coarse)').matches
}

export function clamp(value: number, min: number, max: number) {
  if (value > max) return max
  else if (value < min) return min
  else return value
}

const ZOOM_STEPS = isTouchDevice()
  ? [0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3]
  : [0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2]

const FILE_PREVIEW_SUPPORTED_MIME_TYPES = [
  MIME_TYPES.MIME_TYPE_PDF,
  MIME_TYPES.MIME_TYPE_PNG,
  MIME_TYPES.MIME_TYPE_JPEG,
  MIME_TYPES.MIME_TYPE_GIF,
  MIME_TYPES.MIME_TYPE_WEBP,
  MIME_TYPES.MIME_TYPE_SVG,
  MIME_TYPES.MIME_TYPE_BMP,
  MIME_TYPES.MIME_TYPE_BMP_X_MS,
  MIME_TYPES.MIME_TYPE_DEEPPAY_PAYMENT_ORDER,
  MIME_TYPES.MIME_TYPE_JSON,
  MIME_TYPES.MIME_TYPE_TXT,
  MIME_TYPES.MIME_TYPE_XML,
  MIME_TYPES.MIME_TYPE_APPLICATION_XML,
  MIME_TYPES.MIME_TYPE_MARKDOWN,
  MIME_TYPES.MIME_TYPE_MARKDOWN_X_WEB,
  MIME_TYPES.MIME_TYPE_MARKDOWN_X,
  MIME_TYPES.MIME_TYPE_HTML,
  // email
  MIME_TYPES.MIME_TYPE_EML,
  MIME_TYPES.MIME_TYPE_MSG,
  // video
  MIME_TYPES.MIME_TYPE_VIDEO_MP4,
  MIME_TYPES.MIME_TYPE_VIDEO_WEBM,
  MIME_TYPES.MIME_TYPE_VIDEO_MPEG,
  MIME_TYPES.MIME_TYPE_VIDEO_QUICKTIME,
  MIME_TYPES.MIME_TYPE_VIDEO_MOV,
  // audio
  MIME_TYPES.MIME_TYPE_AUDIO_MPEG,
  MIME_TYPES.MIME_TYPE_AUDIO_WAV,
  MIME_TYPES.MIME_TYPE_AUDIO_WEBM,
  MIME_TYPES.MIME_TYPE_AUDIO_MP4,
]

const OFFICE_PREVIEW_MIME_TYPES = [
  MIME_TYPES.MIME_TYPE_DOC,
  MIME_TYPES.MIME_TYPE_DOCX,
  MIME_TYPES.MIME_TYPE_XLS,
  MIME_TYPES.MIME_TYPE_XLSX,
  MIME_TYPES.MIME_TYPE_PPT,
  MIME_TYPES.MIME_TYPE_PPTX,
]

const VIDEO_MIME_TYPES = [
  MIME_TYPES.MIME_TYPE_VIDEO_MP4,
  MIME_TYPES.MIME_TYPE_VIDEO_WEBM,
  MIME_TYPES.MIME_TYPE_VIDEO_MPEG,
  MIME_TYPES.MIME_TYPE_VIDEO_QUICKTIME,
  MIME_TYPES.MIME_TYPE_VIDEO_MOV,
]

const AUDIO_MIME_TYPES = [
  MIME_TYPES.MIME_TYPE_AUDIO_MPEG,
  MIME_TYPES.MIME_TYPE_AUDIO_WAV,
  MIME_TYPES.MIME_TYPE_AUDIO_WEBM,
  MIME_TYPES.MIME_TYPE_AUDIO_MP4,
]

// the only types handed to DeepPreview; anything else gets the download card
const PREVIEW_RENDERER_MIME_TYPES = [
  // image
  MIME_TYPES.MIME_TYPE_PNG,
  MIME_TYPES.MIME_TYPE_JPEG,
  MIME_TYPES.MIME_TYPE_GIF,
  MIME_TYPES.MIME_TYPE_WEBP,
  MIME_TYPES.MIME_TYPE_SVG,
  MIME_TYPES.MIME_TYPE_BMP,
  MIME_TYPES.MIME_TYPE_BMP_X_MS,
  // text
  MIME_TYPES.MIME_TYPE_TXT,
  MIME_TYPES.MIME_TYPE_XML,
  MIME_TYPES.MIME_TYPE_APPLICATION_XML,
]

const FILE_EDIT_SUPPORTED_MIME_TYPES = [
  MIME_TYPES.MIME_TYPE_DOCX,
  MIME_TYPES.MIME_TYPE_XLSX,
  MIME_TYPES.MIME_TYPE_PPTX,
  // TXT is not supported because the content is not updated from OO on DeepBox
  // MIME_TYPES.MIME_TYPE_TXT
]

/**
 * MIME types can include parameters (e.g. "text/plain;charset=UTF-8"),
 * so we strip everything after the first ";" to get the base type
 * before checking against the supported list.
 */
export const isMimeTypeSupported = (
  mimeType: MimeTypes,
  mimeTypes: MimeTypes[],
) =>
  mimeTypes.some(
    (supported) =>
      mimeType === supported || mimeType.startsWith(`${supported};`),
  )

export const isMimeTypeViewSupported = (mimeType: MimeTypes) =>
  isMimeTypeSupported(mimeType, FILE_PREVIEW_SUPPORTED_MIME_TYPES)

export const isMimeTypeEditSupported = (mimeType: MimeTypes) =>
  isMimeTypeSupported(mimeType, FILE_EDIT_SUPPORTED_MIME_TYPES)

export const isMimeTypeOfficePreview = (mimeType: MimeTypes) =>
  isMimeTypeSupported(mimeType, OFFICE_PREVIEW_MIME_TYPES)

// every markdown type DeepPreview renders, so none falls through to its unsanitized renderer
export const isMimeTypeMarkdown = (mimeType: MimeTypes) =>
  isMimeTypeSupported(mimeType, [
    MIME_TYPES.MIME_TYPE_MARKDOWN,
    MIME_TYPES.MIME_TYPE_MARKDOWN_X_WEB,
    MIME_TYPES.MIME_TYPE_MARKDOWN_X,
  ])

export const isMimeTypeHtml = (mimeType: MimeTypes) =>
  isMimeTypeSupported(mimeType, [MIME_TYPES.MIME_TYPE_HTML])

const isMimeTypeJson = (mimeType: MimeTypes) =>
  isMimeTypeSupported(mimeType, [MIME_TYPES.MIME_TYPE_JSON])

const isMimeTypeTxt = (mimeType: MimeTypes) =>
  isMimeTypeSupported(mimeType, [MIME_TYPES.MIME_TYPE_TXT])

export type PreviewRenderer =
  'pdf' | 'markdown' | 'text' | 'video' | 'audio' | 'download' | 'native'

export function getPreviewRenderer(
  mimeType: MimeTypes,
  size: number,
): PreviewRenderer {
  if (isMimeTypeSupported(mimeType, [MIME_TYPES.MIME_TYPE_PDF])) return 'pdf'
  if (isMimeTypeMarkdown(mimeType)) return 'markdown'
  // DeepPreview would load HTML into an iframe, so HTML too large for the editor is download-only
  if (isMimeTypeHtml(mimeType)) {
    return size < HTML_PREVIEW_MAX_SIZE ? 'text' : 'download'
  }
  if (isMimeTypeJson(mimeType)) {
    return size < JSON_PREVIEW_MAX_SIZE ? 'text' : 'download'
  }
  if (isMimeTypeTxt(mimeType)) {
    return size < HTML_PREVIEW_MAX_SIZE ? 'text' : 'native'
  }
  if (isMimeTypeSupported(mimeType, VIDEO_MIME_TYPES)) return 'video'
  if (isMimeTypeSupported(mimeType, AUDIO_MIME_TYPES)) return 'audio'
  if (isMimeTypeSupported(mimeType, PREVIEW_RENDERER_MIME_TYPES))
    return 'native'
  return 'download'
}

export const isPreviewRendererTextContent = (renderer: PreviewRenderer) =>
  renderer === 'markdown' || renderer === 'text'

export function usePreview(
  defaultPreviewWidth: Ref<number> = ref(DEFAULT_PREVIEW_WIDTH),
) {
  const zoom = ref(1) // base zoom factor (1 = 100%)

  const minZoom = computed(() => ZOOM_STEPS[0])
  const maxZoom = computed(() => ZOOM_STEPS[ZOOM_STEPS.length - 1])
  const zoomPercentage = computed(() => Math.round(toValue(zoom) * 100))

  const previewWidth = computed(() => defaultPreviewWidth.value * zoom.value)

  const previewZoomPercentage = computed(
    () => `${(zoom.value * 100).toFixed(2)}%`,
  )

  function previewZoomIn(currentZoom: number) {
    const next = ZOOM_STEPS.find((value) => currentZoom < value)
    return next ?? ZOOM_STEPS[ZOOM_STEPS.length - 1]
  }

  function previewZoomOut(currentZoom: number) {
    const next = ZOOM_STEPS.findLast((value) => currentZoom > value)
    return next ?? ZOOM_STEPS[0]
  }

  function applyZoom(newZoom: number, maxWidth: number) {
    // Clamp based on max available width
    zoom.value = Math.min(newZoom, maxWidth / defaultPreviewWidth.value)
  }

  return {
    zoom,
    minZoom,
    maxZoom,
    zoomPercentage,
    previewWidth,
    previewZoomPercentage,
    previewZoomIn,
    previewZoomOut,
    applyZoom,
  }
}
